<template>
  <Teleport to="body">
    <div
      v-for="position in positions"
      :key="position"
      :class="getContainerClasses(position)"
    >
      <TransitionGroup
        name="toast"
        tag="div"
        :class="getGroupClasses(position)"
      >
        <div
          v-for="toast in getToastsByPosition(position)"
          :key="toast.id"
          class="toast-wrapper w-full"
        >
          <MeridiaMessage
            type="toast"
            :status="toast.status"
            :size="toast.size"
            :title="toast.title"
            :body="toast.body"
            :close="toast.close ?? true"
            :pt="toast.pt"
            @close="() => handleClose(toast.id)"
          >
            <template v-if="toast.slots?.icon" #icon>
              <component :is="toast.slots.icon" />
            </template>
            <template v-if="toast.slots?.title" #title>
              <component :is="toast.slots.title" />
            </template>
            <template v-if="toast.slots?.body" #body>
              <component :is="toast.slots.body" />
            </template>
            <template v-if="toast.slots?.default" #default>
              <component :is="toast.slots.default" />
            </template>
            <template v-if="toast.slots?.close" #close="{ onClose }">
              <component :is="toast.slots.close" :on-close="onClose" />
            </template>
          </MeridiaMessage>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import MeridiaMessage from '../../components/message/MeridiaMessage.vue';
import { toastService } from './toastService';
import type { ToastPosition } from './types';

const positions: ToastPosition[] = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

const toasts = computed(() => toastService.getToasts());

const getToastsByPosition = (position: ToastPosition) => {
  return toasts.value.filter((toast) => toast.position === position);
};

const getContainerClasses = (position: ToastPosition) => {
  const classes = ['fixed', 'z-[999999999]', 'pointer-events-none'];

  // Position classes
  switch (position) {
    case 'top-left':
      classes.push('top-0', 'left-0');
      break;
    case 'top-center':
      classes.push('top-0', 'left-1/2', '-translate-x-1/2');
      break;
    case 'top-right':
      classes.push('top-0', 'right-0');
      break;
    case 'bottom-left':
      classes.push('bottom-0', 'left-0');
      break;
    case 'bottom-center':
      classes.push('bottom-0', 'left-1/2', '-translate-x-1/2');
      break;
    case 'bottom-right':
      classes.push('bottom-0', 'right-0');
      break;
  }

  return classes;
};

const getGroupClasses = (position: ToastPosition) => {
  const classes = ['flex', 'flex-col', 'gap-s', 'p-m'];

  // Alignment classes
  if (position.includes('center')) {
    classes.push('items-center');
  } else if (position.includes('right')) {
    classes.push('items-end');
  } else {
    classes.push('items-start');
  }

  return classes;
};

const handleClose = (id: string) => {
  toastService.remove(id);
};
</script>

<style scoped>
.toast-wrapper {
  pointer-events: auto;
}

/* Toast transitions */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
